import React from 'react';
import './CustomerStageBadge.css';

// Customer lifecycle stage keys
export type CustomerStageKey = 
  | 'visitor'
  | 'sample-requested'
  | 'sample-sent'
  | 'sample-delivered'
  | 'feedback-given'
  | 'converted'
  | 'loyal'
  | 'churned';

// Configuration for each stage
export interface StageConfigItem {
  label: string;
  color: string;
  icon: string;
  description: string;
}

// Badge size options
export type BadgeSize = 'small' | 'medium' | 'large';

// Props interface for CustomerStageBadge component
export interface CustomerStageBadgeProps {
  stage: CustomerStageKey | string;
  size?: BadgeSize;
  showIcon?: boolean;
  showDescription?: boolean;
}

// Stage configuration object
export const STAGE_CONFIG: Record<CustomerStageKey, StageConfigItem> = {
  'visitor': {
    label: 'Visitor',
    color: '#9E9E9E',
    icon: '👋',
    description: 'Exploring products'
  },
  'sample-requested': {
    label: 'Sample Requested',
    color: '#2196F3',
    icon: '📦',
    description: 'Awaiting sample shipment'
  },
  'sample-sent': {
    label: 'Sample Sent',
    color: '#FF9800',
    icon: '🚚',
    description: 'Sample on the way'
  },
  'sample-delivered': {
    label: 'Sample Delivered',
    color: '#9C27B0',
    icon: '✅',
    description: 'Sample received'
  },
  'feedback-given': {
    label: 'Feedback Given',
    color: '#00BCD4',
    icon: '💬',
    description: 'Shared experience'
  },
  'converted': {
    label: 'Converted',
    color: '#4CAF50',
    icon: '⭐',
    description: 'Made first purchase'
  },
  'loyal': {
    label: 'Loyal Customer',
    color: '#FFD700',
    icon: '👑',
    description: 'Repeat customer'
  },
  'churned': {
    label: 'Inactive',
    color: '#F44336',
    icon: '😴',
    description: 'Needs re-engagement'
  }
};

export default function CustomerStageBadge({ 
  stage, 
  size = 'medium', 
  showIcon = true, 
  showDescription = false 
}: CustomerStageBadgeProps): React.ReactElement {
  const config: StageConfigItem = STAGE_CONFIG[stage as CustomerStageKey] || STAGE_CONFIG['visitor'];
  
  const sizeClass = `stage-badge-${size}`;
  
  return (
    <div className={`customer-stage-badge ${sizeClass}`}>
      <div 
        className="stage-badge-content" 
        style={{ backgroundColor: config.color }}
      >
        {showIcon && <span className="stage-icon">{config.icon}</span>}
        <span className="stage-label">{config.label}</span>
      </div>
      {showDescription && (
        <div className="stage-description">{config.description}</div>
      )}
    </div>
  );
}
